import type { FC } from 'react'
import { useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import type { ConversationTurnEntry } from '@/index'
import { EditToolContent, FileInfo, ToolCodeBlock, ToolHeader } from '../shared'
import { calculateDiffStats, calculateWriteStats } from '@/features/events/shared/utils'
import { getLanguageFromPath } from '../utils/fileModificationUtils'
import { EventType } from '@/features/events/shared/types'
import { BaseBubble } from './BaseBubble'
import { LazyCollapsibleContent } from '../renderers/shared'

interface FileModificationBubbleProps {
  readonly event: ConversationTurnEntry
  readonly showTimestamp?: boolean
  readonly onEventDetailsClick?: (event: ConversationTurnEntry) => void
}

interface WriteToolContentProps {
  readonly content: string
  readonly language: string
  readonly showLineNumbers: boolean
}

interface FileModificationData {
  readonly language: string
  readonly stats:
    | ReturnType<typeof calculateWriteStats>
    | ReturnType<typeof calculateDiffStats>
    | null
  readonly isWriteTool: boolean
}

export const FileModificationBubble: FC<FileModificationBubbleProps> = ({
  event,
  showTimestamp = true,
  onEventDetailsClick,
}) => {
  const { fileChanges, toolName } = event

  const { language, stats, isWriteTool } = useFileModificationData(fileChanges, toolName)

  if (!fileChanges || !toolName || !stats) {
    return null
  }

  return (
    <BaseBubble
      event={event}
      showTimestamp={showTimestamp}
      onEventDetailsClick={onEventDetailsClick}
    >
      <ToolHeader eventType={EventType.FILE_MODIFICATION} toolName={toolName} />

      <FileInfo filePath={fileChanges.targetPath} stats={stats} />

      <Box sx={contentContainerSx}>
        {isWriteTool && fileChanges.newContent && (
          <WriteToolContent
            content={fileChanges.newContent}
            language={language}
            showLineNumbers={true}
          />
        )}
        {!isWriteTool && fileChanges.updates && (
          <EditToolContent
            mode="post-tool"
            updates={fileChanges.updates}
            language={language}
            previewThreshold={30}
          />
        )}
      </Box>
    </BaseBubble>
  )
}

function useFileModificationData(
  fileChanges: ConversationTurnEntry['fileChanges'],
  toolName: ConversationTurnEntry['toolName'],
): FileModificationData {
  const language = useMemo(
    () => (fileChanges ? getLanguageFromPath(fileChanges.targetPath) : 'text'),
    [fileChanges],
  )

  const stats = useMemo(() => {
    if (!fileChanges) return null

    if (toolName === 'Write' && fileChanges.newContent) {
      return calculateWriteStats(fileChanges.newContent)
    }

    if (fileChanges.updates) {
      return calculateDiffStats(fileChanges.updates)
    }

    return null
  }, [fileChanges, toolName])

  const isWriteTool = toolName === 'Write'

  return { language, stats, isWriteTool }
}

const WriteToolContent: FC<WriteToolContentProps> = ({ content, language, showLineNumbers }) => {
  return (
    <Box sx={toolContentContainerSx}>
      <LazyCollapsibleContent content={content}>
        {(displayContent) => (
          <ToolCodeBlock
            code={displayContent}
            language={language}
            showLineNumbers={showLineNumbers}
            diffPrefix="+"
          />
        )}
      </LazyCollapsibleContent>
    </Box>
  )
}

const contentContainerSx: SxProps<Theme> = {
  width: '100%',
}

const toolContentContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
}
